import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Facebook, Linkedin, Link2 } from "lucide-react";
import { api, API, formatDate } from "@/lib/api";
import { ContentBlockRenderer } from "@/components/ContentBlockRenderer";
import { RelatedArticles } from "@/components/RelatedArticles";
import { CommentSection } from "@/components/CommentSection";
import { InlineNewsletterSignup } from "@/components/InlineNewsletterSignup";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";

function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.87.5 3.62 1.44 5.13L2 22l5.13-1.55a9.9 9.9 0 0 0 4.9 1.28h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.8 14.02c-.24.68-1.42 1.3-1.95 1.36-.5.06-1.03.09-1.65-.1-.38-.12-.87-.28-1.5-.55-2.64-1.14-4.36-3.79-4.5-3.97-.13-.18-1.08-1.43-1.08-2.73s.68-1.94.93-2.2c.24-.27.53-.34.71-.34.18 0 .35.001.5.008.16.008.38-.06.6.46.24.55.79 1.94.86 2.08.07.14.12.31.02.5-.1.19-.15.31-.29.48-.14.16-.3.36-.43.48-.14.13-.29.28-.13.55.17.28.75 1.24 1.62 2.01 1.11.99 2.05 1.3 2.34 1.44.29.14.46.12.63-.06.18-.19.75-.87.95-1.17.2-.29.4-.24.67-.14.28.1 1.75.83 2.05.98.29.15.48.22.55.34.08.13.08.72-.16 1.4z" />
    </svg>
  );
}

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.9 2H22l-7.2 8.24L23 22h-6.6l-5.17-6.76L5.2 22H2l7.7-8.8L1.4 2H8l4.66 6.17L18.9 2zm-2.31 18h1.8L7.5 3.9H5.6L16.59 20z" />
    </svg>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [shortUrl, setShortUrl] = useState(null);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    setShortUrl(null);
    api.get(`/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="p-24 text-center font-serif text-2xl text-[#10243A]">Статията не е намерена</div>;
  if (!article) return <div className="p-24 text-center text-[#66717B]">Зареждане...</div>;

  const absoluteUrl = (path) => {
    if (!path) return undefined;
    if (/^https?:\/\//.test(path)) return path;
    return `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
  };
  const isoUtc = (value) => {
    if (!value) return undefined;
    return /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  };

  const getShareUrl = async () => {
    if (shortUrl) return shortUrl;
    try {
      const { data } = await api.post("/shortlinks", { target_url: window.location.href });
      const url = `${API}/s/${data.code}`;
      setShortUrl(url);
      return url;
    } catch {
      return window.location.href;
    }
  };

  const share = async (platform) => {
    const url = await getShareUrl();
    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      toast.success(`Кратък линк копиран: ${url}`);
      return;
    }
    const text = encodeURIComponent(article.title);
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${text}%20${encodeURIComponent(url)}`,
      x: `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[platform], "_blank", "noopener,noreferrer");
  };

  const blocks = article.content_blocks || [];
  const midpoint = Math.ceil(blocks.length / 2);
  const firstHalf = blocks.slice(0, midpoint);
  const secondHalf = blocks.slice(midpoint);

  return (
    <article data-testid="article-page">
      <SEO
        title={article.title}
        description={article.seo_description || article.subtitle}
        image={article.cover_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: article.title,
          description: article.subtitle,
          image: article.cover_image ? [absoluteUrl(article.cover_image)] : undefined,
          author: { "@type": "Person", name: article.author_name },
          publisher: { "@type": "Organization", name: "Metri.bg", logo: { "@type": "ImageObject", url: `${window.location.origin}/logo.jpg` } },
          datePublished: isoUtc(article.published_at),
          dateModified: isoUtc(article.updated_at || article.published_at),
          mainEntityOfPage: { "@type": "WebPage", "@id": window.location.href },
        }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10">
        <div className="text-xs text-[#66717B] mb-3" data-testid="article-breadcrumb">
          <Link to="/" className="hover:text-[#C47A35]">Начало</Link>
          <span className="mx-2">/</span>
          <span className="text-[#C47A35] font-semibold uppercase tracking-wide">{article.category_name}</span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#10243A] leading-[1.12]" data-testid="article-title">
          {article.title}
        </h1>
        {article.subtitle && <p className="mt-4 text-lg sm:text-xl text-[#66717B] leading-relaxed">{article.subtitle}</p>}
        <div className="mt-6 flex items-center justify-between border-y border-[#E4E7E9] py-4">
          <div className="text-sm">
            <span className="font-semibold text-[#10243A]" data-testid="article-author">{article.author_name}</span>
            <span className="text-[#66717B]"> &middot; {formatDate(article.published_at)}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => share("facebook")} className="p-2 text-[#66717B] hover:text-[#C47A35] transition-colors" data-testid="share-facebook-btn"><Facebook className="w-4 h-4" /></button>
            <button onClick={() => share("linkedin")} className="p-2 text-[#66717B] hover:text-[#C47A35] transition-colors" data-testid="share-linkedin-btn"><Linkedin className="w-4 h-4" /></button>
            <button onClick={() => share("whatsapp")} className="p-2 text-[#66717B] hover:text-[#C47A35] transition-colors" data-testid="share-whatsapp-btn"><WhatsAppIcon className="w-4 h-4" /></button>
            <button onClick={() => share("x")} className="p-2 text-[#66717B] hover:text-[#C47A35] transition-colors" data-testid="share-x-btn"><XIcon className="w-4 h-4" /></button>
            <button onClick={() => share("copy")} className="p-2 text-[#66717B] hover:text-[#C47A35] transition-colors" data-testid="share-copy-btn"><Link2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {article.cover_image && (
        <div className="max-w-5xl mx-auto mt-8 px-4 sm:px-6">
          <img src={article.cover_image} alt={article.cover_image_alt || article.title} className="w-full object-cover" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="article-content">
        <ContentBlockRenderer blocks={firstHalf} />
        {secondHalf.length > 0 && <InlineNewsletterSignup source="article_inline" />}
        <ContentBlockRenderer blocks={secondHalf} />

        <RelatedArticles articleId={article.id} />
        <CommentSection articleId={article.id} />
      </div>
    </article>
  );
}
